import { FilePreviewTile } from "@clipboard-health/ui-components";
import { Box, DialogContent, Grid, Stack } from "@mui/material";
import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { Button } from "../../../components/Button";
import { FileUploaderEmptyState } from "../../../components/FileUploaderEmptyState";
import { FileUploaderLimitReached } from "../../../components/FileUploaderLimitReached";
import { PageFooterWrapper } from "../../../components/PageFooterWrapper";
import { PageWrapper } from "../../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../../components/StickyScrollPageHeader";
import { TextButton } from "../../../components/TextButton";
import { invalidateWorkerRequirements } from "../../api/invalidateWorkerRequirements";
import {
  DOCUMENT_DETAILS_FULL_PATH,
  DOCUMENT_PREVIEW_FULL_PATH,
  DOCUMENT_SUBMISSION_APPROVED_PATH,
  DOCUMENT_SUBMISSION_ERROR_PATH,
  DOCUMENT_SUBMISSION_IN_PROGRESS_PATH,
  DOCUMENT_SUBMISSION_IN_REVIEW_PATH,
  DOCUMENT_UPLOAD_MODAL_PATH,
} from "../../paths";
import { useFileUploaderContext } from "../../useFileUploaderContext";
import { AddFileTile } from "../components/AddFileTile";
import { useRequirementDetailsContext } from "../useRequirementDetailsContext";
import { useDocumentUploadFlow } from "./useDocumentUploadFlow";

export function DocumentDetailsUploadPreviewPage() {
  const { uploadedFiles, removeFile, clearFiles, hasReachedMaxFilesCount } =
    useFileUploaderContext();
  const { mutateAsync: uploadDocument } = useDocumentUploadFlow();
  const { requirement, setUploadedDocument } = useRequirementDetailsContext();

  const { id: requirementId = "", attributes } = requirement.requirement ?? {};
  const { name: requirementName = "" } = attributes ?? {};

  const history = useHistory();
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);

  const navigateToUpload = () => {
    history.replace(
      generatePath(`${DOCUMENT_PREVIEW_FULL_PATH}/${DOCUMENT_UPLOAD_MODAL_PATH}`, {
        requirementId,
      })
    );
  };

  const handleCancel = () => {
    clearFiles();

    history.replace(
      generatePath(DOCUMENT_DETAILS_FULL_PATH, {
        requirementId,
      })
    );
  };

  const handleFinish = async () => {
    // opens loading modal on this page
    history.replace(
      generatePath(`${DOCUMENT_PREVIEW_FULL_PATH}/${DOCUMENT_SUBMISSION_IN_PROGRESS_PATH}`, {
        requirementId,
      })
    );

    try {
      const { data } = await uploadDocument({
        requirementName,
        requirementId,
        files: uploadedFiles,
      });

      setUploadedDocument(data);

      logEvent(APP_V2_USER_EVENTS.UPLOAD_DOCUMENT_SUCCESS, {
        requirement_id: requirementId,
      });

      const modalPath =
        data.attributes.status === "APPROVED"
          ? DOCUMENT_SUBMISSION_APPROVED_PATH
          : DOCUMENT_SUBMISSION_IN_REVIEW_PATH;

      void invalidateWorkerRequirements(queryClient);

      history.replace(
        generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${modalPath}`, {
          requirementId,
        })
      );

      clearFiles();
    } catch {
      logEvent(APP_V2_USER_EVENTS.UPLOAD_DOCUMENT_FAILURE, {
        requirement_id: requirementId,
      });

      // shows error modal on this page to be able to easily retry
      history.replace(
        generatePath(`${DOCUMENT_PREVIEW_FULL_PATH}/${DOCUMENT_SUBMISSION_ERROR_PATH}`, {
          requirementId,
        })
      );
    }
  };

  return (
    <PageWrapper variant="tertiary">
      <StickyScrollPageHeader
        shouldWrapTitle
        headerVariant="tertiary"
        title={`Upload files for ${requirementName}`}
        scrollContainerRef={scrollRef}
        onClickBack={() => {
          history.replace(
            generatePath(DOCUMENT_DETAILS_FULL_PATH, {
              requirementId,
            })
          );
        }}
      />

      <DialogContent
        ref={scrollRef}
        sx={{
          padding: 5,
          display: "flex",
          flexDirection: "column",
          gap: 5,

          "> * ": {
            flexShrink: 0,
          },
        }}
      >
        {hasReachedMaxFilesCount && <FileUploaderLimitReached />}

        <Grid container spacing={5} columns={2}>
          {uploadedFiles.map((file) => (
            <Grid key={file.id} item xs={1}>
              <FilePreviewTile
                alt={`Document preview for ${file.id}`}
                fileExtension={file.extension}
                fileName={file.name}
                fileUrl={file.dataUrlBase64}
                onRemove={() => {
                  removeFile(file.id);
                }}
              />
            </Grid>
          ))}
          {!hasReachedMaxFilesCount && (
            <Grid item xs={1}>
              <AddFileTile onClick={navigateToUpload} />
            </Grid>
          )}
        </Grid>

        {uploadedFiles.length === 0 && (
          <Box sx={{ marginY: "auto" }}>
            <FileUploaderEmptyState />
          </Box>
        )}
      </DialogContent>

      <PageFooterWrapper>
        <Stack spacing={5} sx={{ alignItems: "center" }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={uploadedFiles.length === 0}
            onClick={handleFinish}
          >
            Finish and upload
          </Button>

          <TextButton size="large" onClick={handleCancel}>
            Cancel
          </TextButton>
        </Stack>
      </PageFooterWrapper>
    </PageWrapper>
  );
}
